<template>
  <QCard :class="['vehicle-shortlist-card row q-py-md q-px-md', ...(expanded && ['expanded'])].join(' ')" flat bordered>
    <div class="vehicle-image-container">
      <QImg
        v-if="showImage == 'trim'"
        :src="imagePathTrim"
        :contain="trimImgUseContain"
        :cover="trimImgUseCover"
        :placeholder-src="placeholderUrl"
        spinner-color="white"
        class="vehicle-image-content trim"
        @error="handleImageError('trim')"
        trim
      />
      <QImg
        v-if="showImage == 'normal'"
        :src="imagePath"
        contain
        :placeholder-src="placeholderUrl"
        spinner-color="white"
        class="vehicle-image-content normal"
        @error="handleImageError('normal')"
        normal
      />
      <QImg
        v-if="showImage == 'base'"
        :src="imagePathBase"
        contain
        :placeholder-src="placeholderUrl"
        spinner-color="white"
        class="vehicle-image-content base"
        @error="handleImageError('base')"
      />
    </div>
    <div class="vehicle-details-container">
      <div class="vehicle-details-content">
        <div class="vehicle-info">
          <p class="text-grey-8 vehicle-info-fueltype font-nunito">{{ fuelType }}</p>
          <p class="text-secondary vehicle-info-title">{{ vehicleName }}</p>
          <p class="text-grey-8 vehicle-info-text">{{ vehicleDescription }}</p>
        </div>
        <div class="vehicle-pricing">
          <p class="text-grey-8 vehicle-pricing-text vehicle-pricing-text--label">
            <label>
              {{ displayWeeklyPrice ? 'Estimated weekly payment' : 'Estimated monthly payment' }}
            </label>
          </p>
          <div class="vehicle-pricing__row">
            <span class="text-tertiary vehicle-pricing-amount">
              {{ displayWeeklyPrice ? formattedWeekly : formattedMonthly }} {{ GSTText }}
              <span
                role="button"
                tabindex="0"
                class="vehicle-pricing-asterisk"
                @click="showPricingInfo"
                @keydown.enter.prevent="showPricingInfo"
                >*</span
              >
            </span>
            <template v-if="showPaymentBreakdown && pbd_isPaymentBreakdownsEnabled">
              <div class="vehicle-pricing__divider" aria-hidden="true" />
              <PaymentBreakdownButton :vehicle="vehicle" :pay-cycle="repaymentOption" @click="showPaymentBreakdownModal = true" />
            </template>
          </div>
        </div>
      </div>

      <div class="action-group-container">
        <p class="rrp-text">
          RRP: {{ formattedRRP }}
          <QIcon v-if="displayRRPInfo" :name="infoIcon" class="info-icon cursor-pointer" size="20px" @click="showInformation"></QIcon>
        </p>
        <div v-if="showRemoveBtn" class="row items-center mobile-width-100" :style="expanded ? 'gap: 10px' : ''">
          <div :class="expanded ? 'col-12' : 'col-6 col-sm-12 col-md-12 col-lg-6'">
            <CalculatorButton
              class="remove-button"
              color="primary"
              outline
              label="View vehicle"
              rounded
              :styleProp="expanded ? 'width: 100%' : ''"
              @click="$emit('onViewVehicle')"
            />
          </div>
          <div :class="expanded ? 'col-12' : 'col-6 col-sm-12 col-md-12 col-lg-6'">
            <CalculatorButton
              color="primary"
              class="remove-button"
              label="Remove"
              hasChevronIcon
              outlined
              rounded
              :styleProp="expanded ? 'width: 100%' : ''"
              fullWidth
              @click="handleRemoveShortlist"
            />
          </div>
        </div>
        <div v-if="showViewVehicle" class="row items-center vehicle-page-header__variants">
          <div class="col-6">
            <CalculatorButton
              class="remove-button"
              color="primary"
              outline
              label="Select variant"
              rounded
              @click="$emit('onViewVehicle')"
            />
          </div>
          <div class="col-6 text-right">
            <CalculatorButton
              class="remove-button"
              :tertiaryBorder="true"
              :label="isShortlisted ? 'Shortlisted' : 'Shortlist'"
              rounded
              :disabled="!isShortlisted && shortlistAtMax"
              :color="isShortlisted ? 'tertiary' : undefined"
              @click="$emit('onShortlist')"
            />
          </div>
        </div>
      </div>
    </div>

    <PaymentBreakdown
      v-if="showPaymentBreakdown && pbd_isPaymentBreakdownsEnabled"
      v-model="showPaymentBreakdownModal"
      :vehicle-finance-amount="pbd_cardParts ? pbd_cardParts.finance : ''"
      :bundled-running-costs-amount="pbd_cardParts ? pbd_cardParts.bundled : ''"
      :estimated-payment="pbd_cardEstimatedPayment"
      :payment-total-label="pbd_cardTotalLabel"
      :payment-cadence="displayWeeklyPrice ? 'weekly' : 'monthly'"
      @onClose="showPaymentBreakdownModal = false"
    />
  </QCard>
</template>

<script>
import { QCard, QImg, QIcon } from 'eclipx.ui';
import { evaAlertCircleOutline } from '@quasar/extras/eva-icons';
import CalculatorButton from '../CalculatorButton';
import PaymentBreakdownButton from '../PaymentBreakdownButton.vue';
import PaymentBreakdown from '../Modal/PaymentBreakdown.vue';
import { PaymentBreakdownMixin } from '../../../mixins/calculator';
import { REPAYMENT_WEEKLY } from '../../../utils/constants';

export default {
  name: 'VehicleShortlistCard',
  mixins: [PaymentBreakdownMixin],
  components: { QCard, QImg, CalculatorButton, QIcon, PaymentBreakdownButton, PaymentBreakdown },
  props: {
    imagePath: {
      type: String
    },
    imagePathTrim: {
      type: String
    },
    imagePathBase: {
      type: String
    },
    fuelType: {
      type: String
    },
    vehicleName: {
      type: String
    },
    vehicleDescription: {
      type: String
    },
    weeklyPrice: {
      type: Number,
      default: 0
    },
    monthlyPrice: {
      type: Number,
      default: 0
    },
    rrp: {
      type: Number,
      default: 0
    },
    repaymentOption: {
      type: String,
      required: true,
      default: REPAYMENT_WEEKLY
    },
    showGST: {
      type: Boolean,
      default: false
    },
    value: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    expanded: {
      type: Boolean,
      default: false
    },
    displayRRPInfo: {
      type: Boolean,
      default: false
    },
    showRemoveBtn: {
      type: Boolean,
      default: true
    },
    showViewVehicle: {
      type: Boolean,
      default: false
    },
    trimImageContain: {
      type: Boolean,
      default: false
    },
    isShortlisted: {
      type: Boolean,
      default: false
    },
    shortlistAtMax: {
      type: Boolean,
      default: false
    },
    vehicleMake: {
      type: String,
      default: ''
    },
    vehicleFinance: {
      type: Number,
      default: null
    },
    vehicleFinanceWeekly: {
      type: Number,
      default: null
    },
    vehicleRunningCost: {
      type: Number,
      default: null
    },
    vehicleRunningCostWeekly: {
      type: Number,
      default: null
    },
    /** When false, the PaymentBreakdownButton + modal never render (used by variants list). */
    showPaymentBreakdown: {
      type: Boolean,
      default: true
    },
    vehicle: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      displayWeeklyPrice: this.repaymentOption === REPAYMENT_WEEKLY,
      placeholderUrl: `${process.env.BASE_URL}assets/images/vehicleImagePlaceholder.svg`,
      showImage: 'trim',
      infoIcon: evaAlertCircleOutline,
      showPaymentBreakdownModal: false
    };
  },
  computed: {
    /** Required by PaymentBreakdownMixin (`pbd_cardParts`) */
    pbd_ratebookRow() {
      return {
        vehicleFinance: this.vehicleFinance,
        vehicleFinanceWeekly: this.vehicleFinanceWeekly,
        vehicleRunningCost: this.vehicleRunningCost,
        vehicleRunningCostWeekly: this.vehicleRunningCostWeekly
      };
    },
    isNZ() {
      const countryCode = this.orgProfile?.region || 'NZ';
      return countryCode === 'NZ';
    },
    formattedWeekly: function () {
      return this.formatCurrent(this.weeklyPrice);
    },
    formattedMonthly: function () {
      return this.formatCurrent(this.monthlyPrice);
    },
    formattedRRP: function () {
      return this.formatCurrent(this.rrp);
    },
    GSTText: function () {
      if (this.showGST) {
        return this.isNZ ? '(ex. gst)' : '(incl. gst)';
      }

      return '';
    },
    trimImgUseContain() {
      return this.trimImageContain || !this.imagePathTrim;
    },
    trimImgUseCover() {
      return !this.trimImageContain && !!this.imagePathTrim;
    }
  },
  methods: {
    formatCurrent: function (value) {
      // return value.toLocaleString('en-AU', { style: 'currency', currency: this.isNZ ? 'NZD' : 'AUD' });
      return `$${value.toLocaleString('en-AU', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
    },
    handleImageError(imageType) {
      if (imageType == 'trim') {
        this.showImage = 'normal';
      } else if (imageType == 'normal') {
        this.showImage = 'base';
      }
    },
    handleRemoveShortlist() {
      this.$emit('onRemoveShortlist');
    },
    showInformation() {
      this.$emit('onInfoIconClick');
    },
    showPricingInfo() {
      this.$emit('onPricingInfoIconClick');
    }
  },
  watch: {
    repaymentOption(newValue) {
      this.displayWeeklyPrice = newValue === REPAYMENT_WEEKLY;
    }
  },
  created() {}
};
</script>

<style lang="sass">
.vehicle-shortlist-card
  .vehicle-pricing-text--label label
    cursor: default
  .vehicle-pricing__row
    display: flex
    flex-wrap: wrap
    align-items: center
    gap: 10px 12px
    margin-top: 5px
  .vehicle-pricing__divider
    width: 5px
    height: 28px
    // background: var(--color-neutral-grey-4, #e0e0e0)
    flex-shrink: 0
  .vehicle-pricing__bundled-note
    font-size: 12px
    margin: 8px 0 0
    line-height: 1.4
  .vehicle-pricing-asterisk
    cursor: pointer
    margin-left: 2px

.vehicle-shortlist-card:not(.expanded)
  .vehicle-image-container
    height: 194px
    width: 100%

  .vehicle-image-content
    height: 194px
    width: 100%

  .vehicle-details-container
    width: 100%
    display: flex
    flex-flow: row wrap

    .vehicle-details-content
      width: 100%
      text-wrap: pretty
      display: flex
      flex-flow: column wrap
      justify-content: space-between

  .vehicle-info-text
    font-size: 14px

  .vehicle-info-fueltype
    font-size: 16px
    font-weight: 700

  .vehicle-info-title
    font-size: 22px
    font-weight: 700
    margin: 0 0 10px

  .vehicle-pricing-text
    font-size: 14px
    margin: 0 !important

  .vehicle-pricing-amount
    font-size: 18px
    font-weight: 800

  .action-group-container
    width: 100%;
    display: flex
    flex-flow: column wrap
    justify-content: space-between
    margin-top: 10px
    .remove-button
      height: 36px
      align-self: end
      &.calculator-button.q-btn .q-btn__wrapper
        min-height: auto!important
  .rrp-text
    font-size: 16px
    display: flex
    flex-flow: row wrap
    align-items: center
    font-weight: 700
    justify-content: space-between

  .info-icon
    margin-left: 5px

  .calculator-button
    min-width: 150px

  @media (max-width: 1080px)
    .mobile-width-100
      gap: 10px
    .vehicle-image-container
      max-width: 300px
      margin-right: 10px
    .vehicle-details-container
      width: calc(100% - 310px)
      justify-content: space-between
    .vehicle-details-content
      max-width: calc(100% - 160px);
    .action-group-container
      align-items: end
      max-width: 150px;
      margin: 0

  @media (max-width: 768px)
    .vehicle-details-content
      max-width: 100%
    .action-group-container
      align-items: start
      max-width: 100%
      margin-top: 10px
      .remove-button
        align-self: end
  @media (max-width: 768px)
    .mobile-width-100
      width: 100%
      gap: 0px
    .vehicle-details-container
      width: 100%
      .remove-button.calculator-button.q-btn
        // min-width: 50%
    .vehicle-image-container
      max-width: 100%

.vehicle-shortlist-card.expanded
  .vehicle-image-container
    margin-right: 10px
    height: 194px
    max-width: 300px
    width: 100%

  .vehicle-image-content
    height: 194px
    width: 100%

  .vehicle-details-container
    width: 100%
    width: calc(100% - 310px)
    display: flex
    flex-flow: row wrap
    justify-content: space-between

    .vehicle-details-content
      width: 100%
      max-width: calc(100% - 230px);
      text-wrap: pretty
      display: flex
      flex-flow: column wrap
      justify-content: space-between

      .vehicle-info-text
        font-size: 14px

      .vehicle-info-fueltype
        font-size: 16px
        font-weight: 700

      .vehicle-info-title
        font-size: 22px
        font-weight: 700
        margin: 0 0 10px

      .vehicle-pricing-text
        font-size: 14px
        margin: 0 !important

      .vehicle-pricing-amount
        font-size: 18px
        font-weight: 800

    .action-group-container
      max-width: 200px;
      width: 100%;
      display: flex
      flex-flow: column wrap
      justify-content: space-between
      margin: 10px 10px 10px 0

      .rrp-text
        display: flex
        flex-flow: row wrap
        align-items: center
        font-weight: 700
        justify-content: space-between

        .info-icon
          margin-left: 5px
          rotate: 180deg

  @media (max-width: 1024px)
    .vehicle-details-content
      max-width: 100%!important
      width: 100%
    .action-group-container
      width: 100%
      max-width: 100%

  @media (max-width: 647px)
    .vehicle-image-container
      margin-right: 0
      margin-bottom: 10px
      max-width: 768px
      .vehicle-image-content
        max-width: 768px
    .vehicle-details-container
      width: 100%

    .action-group-container .shortlist-button
      margin-top: 10px
      max-width: calc(50% + 1em)

  @media (max-width: 374px)
    .action-group-container .shortlist-button
      max-width: 100%
</style>
